import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import HomePage from './pages/HomePage';
import CategoryPage from './pages/CategoryPage';
import ToolPage from './pages/ToolPage';
import ToolsPage from './pages/ToolsPage';
import ResearchPage from './pages/ResearchPage';
import DeliveryPage from './pages/DeliveryPage';
import DesignPage from './pages/DesignPage';
import ResourcesPage from './pages/ResourcesPage';
import IdeationPage from './pages/design/IdeationPage';
import DiscoveryPage from './pages/problem/discovery';
import FramingPage from './pages/problem/framing';
import PrioritisationPage from './pages/problem/prioritisation';
import ProjectScopingPage from './pages/problem/project-scoping/ProjectScopingPage';
import WarmUpPage from './pages/design/ideation/warm-up/WarmUpPage';
import SketchingPage from './pages/design/ideation/sketching';
import StoryboardingPage from './pages/design/ideation/storyboarding';
import DesignStudioPage from './pages/design/ideation/design-studio/DesignStudioPage';
import OrganisationMappingPage from './pages/mapping/organisation-mapping/OrganisationMappingPage';
import ResearchMappingPage from './pages/mapping/research-mapping/ResearchMappingPage';
import UserMappingPage from './pages/mapping/user-mapping/UserMappingPage';
import InvestigatePage from './pages/empathise/investigate';
import UserInterviewPage from './pages/empathise/user-interview';
import UserInsightsPage from './pages/empathise/user-insights';
import DataPage from './pages/empathise/data';
import ProblemReframingPage from './pages/synthesis/problem-reframing';
import DefinePage from './pages/synthesis/define';
import FeaturePrioritisationPage from './pages/synthesis/feature-prioritisation';
import NavigationProvider from './context/NavigationContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import HashRedirect from './components/common/HashRedirect';

// Define redirects for parent categories
const redirects = {
  research: ['empathise', 'mapping', 'synthesis'],
  design: ['sketching', 'storyboarding', 'design-studio'],
  delivery: ['agile', 'events', 'artifacts'],
  resources: ['R&D', 'websites', 'project-tracking', 'further-resources']
};

// Define edge case redirects that need special handling
const edgeCases = [
  { from: '/problem', to: '/research', hash: 'problem' },
  { from: '/empathise', to: '/research', hash: 'empathise' },
  { from: '/mapping', to: '/research', hash: 'mapping' },
  { from: '/synthesis', to: '/research', hash: 'synthesis' },
  { from: '/ideation', to: '/design', hash: 'ideation' },
  { from: '/ideation/warm-up', to: '/design/ideation/warm-up', hash: '' },
  { from: '/ideation/sketching', to: '/design/ideation/sketching', hash: '' },
  { from: '/ideation/storyboarding', to: '/design/ideation/storyboarding', hash: '' },
  { from: '/ideation/design-studio', to: '/design/ideation/design-studio', hash: '' },
  { from: '/deliver', to: '/delivery', hash: '' }
];

function App() {
  return (
    <ErrorBoundary>
      <NavigationProvider>
        <Header />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/category/:categoryId" element={<CategoryPage />} />
          <Route path="/tool/:toolId" element={<ToolPage />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/delivery" element={<DeliveryPage />} />
          <Route path="/design" element={<DesignPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          
          {/* Research section routes - grandparent/parent/child */}
          <Route path="/research/problem/discovery" element={<DiscoveryPage />} />
          <Route path="/research/problem/framing" element={<FramingPage />} />
          <Route path="/research/problem/prioritisation" element={<PrioritisationPage />} />
          <Route path="/research/problem/project-scoping" element={<ProjectScopingPage />} />
          <Route path="/research/problem/:toolId" element={<ToolPage />} />
          <Route path="/research/empathise/:toolId" element={<ToolPage />} />
          <Route path="/research/mapping/:toolId" element={<ToolPage />} />
          <Route path="/research/synthesis/problem-reframing" element={<ProblemReframingPage />} />
          <Route path="/research/synthesis/define" element={<DefinePage />} />
          <Route path="/research/synthesis/feature-prioritisation" element={<FeaturePrioritisationPage />} />
          <Route path="/research/synthesis/:toolId" element={<ToolPage />} />
          
          {/* Empathise section routes - corrected for proper hierarchy */}
          <Route path="/research/empathise/investigate" element={<InvestigatePage />} />
          <Route path="/research/empathise/user-interview" element={<UserInterviewPage />} />
          <Route path="/research/empathise/user-insights" element={<UserInsightsPage />} />
          <Route path="/research/empathise/data" element={<DataPage />} />
          <Route path="/empathise/investigate" element={<Navigate to="/research/empathise/investigate" replace />} />
          <Route path="/empathise/user-interview" element={<Navigate to="/research/empathise/user-interview" replace />} />
          <Route path="/empathise/user-insights" element={<Navigate to="/research/empathise/user-insights" replace />} />
          <Route path="/empathise/data" element={<Navigate to="/research/empathise/data" replace />} />
          
          {/* Design section routes - grandparent/parent/child */}
          <Route path="/design/ideation" element={<IdeationPage />} />
          <Route path="/design/ideation/warm-up" element={<WarmUpPage />} />
          <Route path="/design/ideation/sketching" element={<SketchingPage />} />
          <Route path="/design/ideation/storyboarding" element={<StoryboardingPage />} />
          <Route path="/design/ideation/design-studio" element={<DesignStudioPage />} />
          <Route path="/design/ideation/warm-up/:toolId" element={<ToolPage />} />
          <Route path="/design/ideation/sketching/:toolId" element={<ToolPage />} />
          <Route path="/design/ideation/storyboarding/:toolId" element={<ToolPage />} />
          <Route path="/design/ideation/design-studio/:toolId" element={<ToolPage />} />
          
          {/* Mapping section routes - corrected for proper hierarchy */}
          <Route path="/research/mapping/organisation-mapping" element={<OrganisationMappingPage />} />
          <Route path="/research/mapping/research-mapping" element={<ResearchMappingPage />} />
          <Route path="/research/mapping/user-mapping" element={<UserMappingPage />} />
          
          {/* Redirect old mapping URLs to new structure */}
          <Route path="/mapping/organisation-mapping" element={<Navigate to="/research/mapping/organisation-mapping" replace />} />
          <Route path="/mapping/research-mapping" element={<Navigate to="/research/mapping/research-mapping" replace />} />
          <Route path="/mapping/user-mapping" element={<Navigate to="/research/mapping/user-mapping" replace />} />
          
          {/* Routes for just the category pages without specific tools */}
          <Route path="/design/ideation/sketching" element={<IdeationPage />} />
          <Route path="/design/ideation/storyboarding" element={<IdeationPage />} />
          <Route path="/design/ideation/design-studio" element={<IdeationPage />} />
          
          <Route path="/design/sketching/:toolId" element={<ToolPage />} />
          <Route path="/design/storyboarding/:toolId" element={<ToolPage />} />
          <Route path="/design/design-studio/:toolId" element={<ToolPage />} />
          
          {/* Direct parent redirects - these should not interfere with deeper paths */}
          <Route path="/problem" element={<Navigate to="/research#problem" replace />} />
          <Route path="/ideation" element={<Navigate to="/design#ideation" replace />} />
          
          {/* Redirects for old paths to maintain backward compatibility */}
          <Route path="/problem/discovery" element={<Navigate to="/research/problem/discovery" replace />} />
          <Route path="/problem/framing" element={<Navigate to="/research/problem/framing" replace />} />
          <Route path="/problem/prioritisation" element={<Navigate to="/research/problem/prioritisation" replace />} />
          <Route path="/problem/project-scoping" element={<Navigate to="/research/problem/project-scoping" replace />} />
          <Route path="/synthesis/problem-reframing" element={<Navigate to="/research/synthesis/problem-reframing" replace />} />
          <Route path="/synthesis/define" element={<Navigate to="/research/synthesis/define" replace />} />
          <Route path="/synthesis/feature-prioritisation" element={<Navigate to="/research/synthesis/feature-prioritisation" replace />} />
          
          {/* Add dynamic redirects for parent categories */}
          {Object.entries(redirects).map(([grandparent, parents]) => 
            parents.map(parent => (
              <Route 
                key={`${grandparent}-${parent}`} 
                path={`/${parent}`}
                element={<Navigate to={`/${grandparent}#${parent}`} replace />} 
              />
            ))
          )}

          {/* Handle edge cases */}
          {edgeCases.map(({ from, to, hash }) => (
            <Route
              key={from}
              path={from}
              element={<HashRedirect to={to} hash={hash} />}
            />
          ))}

          {/* Catch legacy tool URLs and redirect to their new location */}
          <Route path="/:category/:subcategory/:tool" element={<ToolPage />} />
          
          {/* Catch-all route for any undefined routes */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Footer />
      </NavigationProvider>
    </ErrorBoundary>
  );
}

export default App; 